import { useMemo } from 'react';
import { Icon, type IconName } from '../editor/chrome/icons';
import { tileUrl } from '../editor/content/tileUrl';
import { Spinner } from '../ui/controls';
import { relativeWhen, type FileRow } from '../home/rows';
import { Thumb } from '../home/parts';
import { type LayerFile } from './layerFile';

/**
 * The /layers page as it was drawn before the kit's ReceivePage (29 Oct), unchanged: shown only while the installed
 * @flobi/asset-library is older than the receive helpers, so /layers keeps working through the rollout. Delete this
 * file once every deploy of Artwork installs a kit with ReceivePage (LayersImport.tsx then never reaches it).
 */
/** The page itself, given what it shows (the lab draws it with no sign-in: lab/LayersLab). */
export function LegacyLayersChooser({ file, problem, files, query, onQuery, going, onNew, onPick, onBack }: {
  file: LayerFile | null; problem: string | null; files: FileRow[] | null; query: string; onQuery: (query: string) => void;
  going: string | null; onNew: () => void; onPick: (row: FileRow) => void; onBack: () => void;
}) {
  const shown = useMemo(() => {
    const words = query.trim().toLowerCase();
    return (files ?? []).filter((row) => !words || row.name.toLowerCase().includes(words));
  }, [files, query]);
  return (
    <main className="min-h-screen bg-page text-label">
      <header className="sticky top-0 z-10 flex h-[56px] items-center gap-[12px] bg-page/90 px-[16px] backdrop-blur sm:px-[28px]">
        <button type="button" onClick={onBack} className="glass flex h-[32px] items-center gap-[6px] rounded-full pl-[10px] pr-[14px] text-[13px] text-label-2 hover:text-label">
          <Icon name="back" size={14} />
          Artwork
        </button>
      </header>

      <div className="mx-auto grid w-full max-w-[1120px] gap-[32px] px-[16px] pb-[48px] pt-[12px] sm:px-[28px] grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)] lg:gap-[48px]">
        {/* What arrives: the picture, then its layers top first, as the Layers panel lists them. */}
        <section aria-label="The layers" className="min-w-0 lg:sticky lg:top-[72px] lg:self-start">
          <div className="overflow-hidden rounded-[16px] bg-fill-1 ring-1 ring-separator">
            <div className="grid aspect-square max-h-[60vh] w-full place-items-center bg-canvas p-[20px] lg:max-h-none">
              {file?.preview ? (
                <img src={tileUrl(file.preview, 800)} alt="" className="max-h-full max-w-full rounded-[6px] object-contain shadow-[0_8px_28px_-10px_rgb(0_0_0/0.6)]" />
              ) : (
                <span className="text-label-4">{problem ? <Icon name="layers" size={28} /> : <Spinner size={16} />}</span>
              )}
            </div>
            <div className="flex items-center gap-[8px] border-t border-separator px-[14px] py-[10px] text-[12px] text-label-2">
              <Icon name="layers" size={14} />
              <span className="flex-1">{file ? `${file.count} ${file.count === 1 ? 'layer' : 'layers'}` : 'Layers'}</span>
              {file ? <span className="tabular-nums text-label-3">{`${file.size.width} × ${file.size.height}`}</span> : null}
            </div>
            {file ? <LayerList file={file} /> : null}
          </div>
        </section>

        {/* Where it goes. */}
        <section aria-label="Where to open them" className="min-w-0">
          <h1 className="text-[26px] font-bold leading-[32px] tracking-[-0.01em]">Open in Artwork</h1>
          <p className="mt-[6px] max-w-[520px] text-[14px] leading-[20px] text-label-2">
            The layers go on a new artboard {file ? `${file.size.width} × ${file.size.height}` : 'the size of the picture'}
            {file?.texts ? ', with the text still editable' : ''}. Choose a new file or one of yours.
          </p>

          {problem ? (
            <div role="alert" className="mt-[24px] flex items-start gap-[10px] rounded-[12px] bg-fill-1 p-[14px] text-[13px]">
              <span className="text-label-2"><Icon name="warning" size={16} /></span>
              <span className="flex-1">{problem}</span>
            </div>
          ) : null}

          <button type="button" disabled={!file || !!going} onClick={onNew}
            className="group mt-[24px] flex w-full items-center gap-[16px] rounded-[14px] bg-fill-1 p-[14px] text-left ring-1 ring-separator transition hover:ring-2 hover:ring-accent disabled:opacity-60 disabled:hover:ring-1 disabled:hover:ring-separator">
            <span className="grid size-[64px] shrink-0 place-items-center rounded-[10px] bg-accent text-white">
              {going === 'new' ? <Spinner size={16} /> : <Icon name="plus" size={24} />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold">{going === 'new' ? 'Making the file…' : 'New file'}</span>
              <span className="block text-[13px] text-label-2">A file of its own, with the layers on its first artboard</span>
            </span>
            <span className="text-label-3 group-hover:text-label"><Icon name="chevron-right" size={16} /></span>
          </button>

          <div className="mt-[32px] flex flex-wrap items-center gap-[12px]">
            <h2 className="flex-1 text-[17px] font-semibold">Add to one of your files</h2>
            <label className="field-focus flex h-[32px] w-full items-center gap-[7px] rounded-full bg-fill-1 px-[11px] sm:w-[240px]">
              <span className="text-label-3"><Icon name="search" size={14} /></span>
              <input type="search" value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Search your files" aria-label="Search your files"
                className="w-0 min-w-0 flex-1 bg-transparent text-[13px] text-label outline-none placeholder:text-label-3" />
            </label>
          </div>

          {files === null ? (
            <p className="mt-[20px] flex items-center gap-[8px] text-[13px] text-label-2"><Spinner size={12} />Loading your files…</p>
          ) : !shown.length ? (
            <p className="mt-[20px] text-[13px] text-label-2">{query ? `No file matches “${query.trim()}”.` : 'No files yet. Start a new one above.'}</p>
          ) : (
            <ul className="mt-[16px] grid gap-x-[16px] gap-y-[20px]" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(168px, calc(50% - 8px)), 1fr))' }}>
              {shown.map((row) => (
                <li key={row.id} className="min-w-0">
                  <button type="button" disabled={!file || !!going} onClick={() => onPick(row)} aria-label={`Add the layers to ${row.name || 'Untitled'}`}
                    className="group block w-full text-left disabled:opacity-60">
                    <span className="relative block">
                      <Thumb url={row.thumbnailUrl} className="aspect-[4/3] rounded-[12px]" />
                      {going === row.id ? (
                        <span className="absolute inset-0 grid place-items-center rounded-[12px] bg-black/40 text-white"><Spinner size={16} /></span>
                      ) : (
                        <span className="glass absolute bottom-[8px] right-[8px] flex h-[26px] items-center gap-[4px] rounded-full px-[9px] text-[12px] font-medium opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 pointer-coarse:opacity-100">
                          <Icon name="add-artboard" size={13} />Add here
                        </span>
                      )}
                    </span>
                    <span className="mt-[8px] block truncate px-[2px] text-[13px] font-medium">{row.name || 'Untitled'}</span>
                    <span className="block truncate px-[2px] text-[11px] text-label-2">{relativeWhen(row.myLastEditAt || row.updatedAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}

/** The layers it brings, top first: a picture's own thumbnail, a T and its words for text, a swatch for a flat colour. */
function LayerList({ file }: { file: LayerFile }) {
  const layers = [...file.document.layers].sort((a, b) => (b.z ?? 0) - (a.z ?? 0));
  return (
    <ul className="max-h-[260px] overflow-y-auto border-t border-separator py-[4px]">
      {layers.map((layer, index) => {
        const picture = layer.kind === 'image' && typeof layer.result === 'number' ? file.pictures[layer.result] : undefined;
        const icon: IconName = layer.kind === 'text' ? 'text' : layer.kind === 'fill' ? 'square' : 'nav-image';
        const name = layer.kind === 'text' ? layer.text?.replace(/\s+/g, ' ').trim() || layer.name || 'Text' : layer.name || picture?.name || `Layer ${layers.length - index}`;
        return (
          <li key={layer.id ?? index} className={`flex h-[34px] items-center gap-[10px] px-[14px] text-[13px] ${layer.visible === false ? 'text-label-3' : ''}`}>
            {picture ? (
              <img src={tileUrl(picture.url, 80)} alt="" className="size-[22px] shrink-0 rounded-[4px] bg-fill-2 object-cover ring-1 ring-separator" />
            ) : layer.kind === 'fill' ? (
              <span className="size-[22px] shrink-0 rounded-[4px] ring-1 ring-separator" style={{ background: layer.fill || 'transparent' }} />
            ) : (
              <span className="grid size-[22px] shrink-0 place-items-center rounded-[4px] bg-fill-2 text-label-2"><Icon name={icon} size={13} /></span>
            )}
            <span className="min-w-0 flex-1 truncate">{name}</span>
            {layer.kind === 'text' ? <span className="shrink-0 text-[11px] text-label-3">Editable</span> : null}
            {layer.visible === false ? <span className="shrink-0 text-[11px] text-label-3">Hidden</span> : null}
          </li>
        );
      })}
    </ul>
  );
}
